import Button, { type Props as ButtonProps } from "~/components/ui/Button"
import { sendEvent } from "~/sdk/analytics"
import { useUI } from "~/sdk/useUI"
import { useSubscription } from "~/sdk/useSubscription"
import { useSignal } from "~/sdk/signal"
import { ImageWidget } from "~/types/widgets"
import { getCart } from "~/hooks/useCart"
import Image from "~/components/ui/Image"
import { toAnalytics } from "~/sdk/ga4/transform/toAnalytics"
import type { ToAddToCart } from "~/sdk/ga4/events/add_to_cart"
interface Item { id: string; seller: string; quantity: number }
import type React from "react"
import { useCallback } from "react"

export type Props = ButtonProps & {
    products: Item[]
    onAddItem?: () => unknown
    icon?: ImageWidget
    iconWidth?: number
    iconHeight?: number
    variant?: 'pdp'
    text?: string
    analytics?: ToAddToCart
}

export default function AddToCartButton({
    products,
    onAddItem,
    icon,
    iconWidth = 16,
    iconHeight = 16,
    text = 'COMPRAR',
    analytics,
    ...props
}: Props) {
    const loading = useSignal(false)
    const { displayCart } = useUI()
    // Non-reactive: cart is only read inside the click handler (to find
    // subscription SKUs to swap). The button itself doesn't render any
    // cart-derived data, so subscribing via useCart() would just add
    // useStore overhead per PDP — and several PDPs mount multiple
    // AddToCartButtons (BuyButton, AddToCartWithQuantitySelector,
    // BuyTogether), so this multiplies fast.
    const { addItems, cart, updateItems } = getCart()
    const showEmptyError = useSignal(false)
    const { subscriptionSkus } = useSubscription()

    const handleClick = useCallback(async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        e.stopPropagation()
        loading.value = true

        try {
            if (products.length === 0) {
                showEmptyError.value = true
                return
            }

            showEmptyError.value = false

            const { items = [] } = cart?.value ?? {}

            const SUBSCRIPTION_SKUS: Record<string, string> = subscriptionSkus.value.reduce(
                (acc, sku, index, array) => {
                    const nextIndex = (index + 1) % array.length
                    acc[sku] = array[nextIndex]
                    return acc
                },
                {} as Record<string, string>,
            )

            const mainProductSku = products[0]?.id
            const skuToRemove = SUBSCRIPTION_SKUS[mainProductSku] ?? null

            if (skuToRemove) {
                const itemsToRemove = items.reduce(
                    (acc, { id }, index) => {
                        if (id === skuToRemove) {
                            acc.push({ id: Number(id), index, quantity: 0 })
                        }
                        return acc
                    },
                    [] as Array<{ id: number; index: number; quantity: number }>,
                )

                if (itemsToRemove.length > 0) {
                    await updateItems({ orderItems: itemsToRemove })
                }
            }

            onAddItem?.()

            await addItems({ orderItems: products })

            if (analytics) {
                const add_to_cart = toAnalytics({
                    type: 'add_to_cart',
                    data: analytics,
                })

                sendEvent(add_to_cart)
            }

            displayCart.value = true
        } catch (err) {
            console.error(`[AddToCartButton] failed:`, err)
        } finally {
            loading.value = false
        }
    }, [products, onAddItem])

    return (
        <>
            <Button {...props} onClick={handleClick} data-deco='add-to-cart'>
                {icon
                    ? (
                        <>
                            <Image
                                src={icon}
                                alt=''
                                width={iconWidth}
                                height={iconHeight}
                                loading='lazy'
                            />
                            <span>COMPRAR</span>
                        </>
                    )
                    : text}
            </Button>

            {showEmptyError.value && (
                <div className='bg-[#fff6f6] border border-[#e38f8f] text-[#c21010] px-3 py-1 mt-3 text-xs w-fit font-bold'>
                    Selecione um produto primeiro
                </div>
            )}
        </>
    )
}
